import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Globe2, Loader2, Save, Mail } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/api";
import { formatDate } from "@/lib/format";

export const DirectoryOptIn = () => {
  const [profile, setProfile] = useState(null);
  const [expertiseText, setExpertiseText] = useState("");
  const [saving, setSaving] = useState(false);
  const [requests, setRequests] = useState([]);

  useEffect(() => {
    api.get("/directory/me").then(({ data }) => {
      setProfile(data);
      setExpertiseText((data.expertise || []).join(", "));
    }).catch((e) => console.error("directory profile load failed", e));
    api.get("/directory/intro-requests").then(({ data }) => setRequests(data)).catch(() => {});
  }, []);

  if (!profile) return null;

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/directory/me", {
        opted_in: profile.opted_in, firm: profile.firm || "", bio: profile.bio || "",
        expertise: expertiseText.split(",").map((t) => t.trim()).filter(Boolean),
      });
      setProfile(data);
      toast.success(data.opted_in ? "You're listed in the investor directory" : "Directory profile saved (not listed)");
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not save profile"); }
    finally { setSaving(false); }
  };

  return (
    <Card className="mt-8 rounded-xl border bg-card p-5" data-testid="directory-optin-card">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Globe2 className="h-4 w-4 text-primary" />
          <h2 className="text-sm font-semibold text-foreground">Investor directory</h2>
        </div>
        <label className="flex items-center gap-2 text-xs text-muted-foreground">
          Listed
          <Switch checked={!!profile.opted_in} onCheckedChange={(v) => setProfile((p) => ({ ...p, opted_in: v }))} data-testid="directory-optin-switch" />
        </label>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">Opt in so founders on tenx can request warm intros. Your email stays hidden until you reply.</p>
      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <Label>Firm</Label>
          <Input value={profile.firm || ""} onChange={(e) => setProfile((p) => ({ ...p, firm: e.target.value }))} placeholder="Angel / Fund name" className="mt-1" data-testid="directory-firm" />
        </div>
        <div>
          <Label>Expertise (comma separated)</Label>
          <Input value={expertiseText} onChange={(e) => setExpertiseText(e.target.value)} placeholder="fintech, hiring, GTM" className="mt-1" data-testid="directory-expertise" />
        </div>
      </div>
      <div className="mt-3">
        <Label>Short bio</Label>
        <Textarea value={profile.bio || ""} onChange={(e) => setProfile((p) => ({ ...p, bio: e.target.value }))} rows={2} className="mt-1" data-testid="directory-bio" />
      </div>
      <Button size="sm" className="mt-3 gap-1.5" onClick={save} disabled={saving} data-testid="directory-save">
        {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save profile
      </Button>

      {requests.length > 0 && (
        <div className="mt-5 border-t pt-4">
          <h3 className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground"><Mail className="h-3.5 w-3.5" /> Intro requests</h3>
          <div className="space-y-2">
            {requests.map((r) => (
              <div key={r.id} className="rounded-lg bg-secondary/60 p-3 text-xs" data-testid={`intro-request-${r.id}`}>
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-foreground">{r.founder_name}{r.company_name ? ` · ${r.company_name}` : ""}</span>
                  <Badge variant="secondary" className="font-normal">{formatDate(r.created_at)}</Badge>
                </div>
                <p className="mt-1 text-foreground">{r.message}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
};
